import { ChevronDown } from 'lucide-react';
import { Accordion as RadixAccordion } from 'radix-ui';
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';
import { Flex } from '@radix-ui/themes';
import { cn } from '@/lib/utils';

export interface AccordionItemProps {
  value: string;
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: number;
  children: ReactNode;
  className?: string;
}

export interface AccordionProps {
  type?: 'single' | 'multiple';
  children: ReactNode;
  className?: string;
  defaultValue?: string | string[];
  gap?: React.ComponentProps<typeof Flex>['gap'];
}

type AccordionItemComponent = (props: AccordionItemProps) => ReactElement;

const AccordionItemContext = createContext<AccordionItemComponent | null>(null);

/** Forwards to the AccordionItem defined inside Accordion (compound API). */
const AccordionItemSlot = (props: AccordionItemProps) => {
  const Item = useContext(AccordionItemContext);
  if (!Item) {
    throw new Error('Accordion.Item must be rendered inside Accordion.');
  }
  return Item(props);
};

/** v90 `.spot-cat` — Radix Accordion primitive + prototype styling. */
export function Accordion({
  type = 'multiple',
  children,
  className,
  defaultValue,
  gap = '2',
}: AccordionProps) {
  const AccordionItem = ({
    value,
    title,
    subtitle,
    badge,
    children: itemChildren,
    className: itemClassName,
  }: AccordionItemProps) => {
    const hasSelection = badge != null && badge > 0;

    return (
      <RadixAccordion.Item
        value={value}
        className={cn('spot-cat', hasSelection && 'sel-any', itemClassName)}
      >
        <RadixAccordion.Header>
          <RadixAccordion.Trigger className="spot-cat-head w-full border-0 bg-transparent text-left">
            <div className="spot-cat-info">
              <div className="spot-cat-name">
                {title}
                {subtitle ? <span className="spot-cat-tag">{subtitle}</span> : null}
              </div>
            </div>
            {badge != null ? (
              <span className="spot-cat-badge">
                <span className="spot-cat-count-num">{badge}</span>
              </span>
            ) : null}
            <ChevronDown className="spot-cat-chev" size={12} aria-hidden />
          </RadixAccordion.Trigger>
        </RadixAccordion.Header>
        <RadixAccordion.Content className="spot-cat-body flex flex-wrap gap-2 px-4 pb-4">
          {itemChildren}
        </RadixAccordion.Content>
      </RadixAccordion.Item>
    );
  };

  return (
    <AccordionItemContext.Provider value={AccordionItem}>
      <Flex direction="column" gap={gap} asChild className={className}>
        <RadixAccordion.Root type={type} defaultValue={defaultValue as never}>
          {children}
        </RadixAccordion.Root>
      </Flex>
    </AccordionItemContext.Provider>
  );
}

Accordion.Item = AccordionItemSlot;
